import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { CONTENT_SCORE_QUESTIONS, scoreContentSystem } from "@/lib/content-score";
import { ResultCard } from "@/components/tools/ResultCard";
import { LightSectionBackdrop } from "@/components/LightSectionBackdrop";
import { DarkSectionBackdrop } from "@/components/DarkSectionBackdrop";

export const Route = createFileRoute("/tools/content-system-score")({
  head: () => ({
    meta: [
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://scottcbusiness.com/tools/content-system-score" },
      { name: "twitter:title", content: "Content System Readiness Score | Scott Cunningham" },
      { name: "twitter:description", content: "Find out whether your content is planned, repeatable, measurable, or still stuck in reactive posting. Free 2-minute diagnostic." },

      { title: "Content System Readiness Score | Scott Cunningham" },
      {
        name: "description",
        content:
          "Find out whether your content is planned, repeatable, measurable, or still stuck in reactive posting. Free 2-minute diagnostic.",
      },
      { property: "og:title", content: "Content System Readiness Score" },
      {
        property: "og:description",
        content:
          "Free 2-minute diagnostic that shows whether your content is random, reactive, planned, or a real engine.",
      },
    ],
    links: [{ rel: "canonical", href: "https://scottcbusiness.com/tools/content-system-score" }],
  }),
  component: ContentSystemScorePage,
});

function ContentSystemScorePage() {
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const allAnswered = CONTENT_SCORE_QUESTIONS.every((q) => answers[q.id]);
  const result = useMemo(
    () => (submitted ? scoreContentSystem(answers) : null),
    [submitted, answers],
  );

  async function handleSubmit() {
    if (!allAnswered || loading) return;
    setLoading(true);
    const computed = scoreContentSystem(answers);
    const startedAt = Date.now();
    try {
      await fetch("/api/public/tool-submission", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tool: "content-system-score",
          score: computed.percentage,
          level: computed.level.key,
          inputSummary: `total=${computed.total}/40`,
          consumeSlot: false,
        }),
      });
    } catch {
      /* still show result locally */
    }
    // Hold the loading state 2–4s for tactile feel
    const elapsed = Date.now() - startedAt;
    if (elapsed < 2200) await new Promise((r) => setTimeout(r, 2200 - elapsed));
    setSubmitted(true);
    setLoading(false);
  }

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <section className="relative isolate overflow-hidden bg-foreground text-cream">
        <DarkSectionBackdrop />
        <div className="relative mx-auto max-w-3xl px-6 py-16 sm:py-20">
          <Link
            to="/tools"
            className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.2em] text-cream/70 hover:text-cream"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> All tools
          </Link>
          <h1 className="font-display mt-4 text-4xl text-cream sm:text-5xl">
            Content System <span className="text-gradient">Readiness</span> Score
          </h1>
          <p className="mt-4 max-w-2xl text-lg text-cream/75">
            Find out whether your content is planned, repeatable, measurable, or still stuck in
            reactive posting.
          </p>
        </div>
      </section>

      <section className="relative isolate flex-1 overflow-hidden">
        <LightSectionBackdrop variant="dots" arcs={false} sparkles={false} />
        <div className="mx-auto max-w-3xl px-6 py-12">
          {submitted && result ? (
            <ResultCard
              tool="content-system-score"
              score={result.percentage}
              levelLabel={result.level.label}
              diagnosis={result.level.diagnosis}
              gaps={result.gaps}
              nextStep={result.level.nextStep}
              cta={result.level.cta}
            />
          ) : (
            <div className="space-y-8">
              {CONTENT_SCORE_QUESTIONS.map((q, i) => (
                <fieldset
                  key={q.id}
                  className="rounded-2xl border border-border bg-card p-6"
                  style={{ boxShadow: "var(--shadow-soft)" }}
                >
                  <legend className="px-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Q{i + 1}
                  </legend>
                  <p className="font-display text-xl text-ink">{q.question}</p>
                  <div className="mt-4 grid gap-2">
                    {q.options.map((opt, idx) => {
                      const points = idx + 1;
                      const selected = answers[q.id] === points;
                      return (
                        <label
                          key={opt}
                          className={`flex cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 text-sm transition ${
                            selected
                              ? "border-primary bg-primary/5 text-ink"
                              : "border-border bg-background text-ink/80 hover:border-ink/30"
                          }`}
                        >
                          <input
                            type="radio"
                            name={q.id}
                            value={points}
                            checked={selected}
                            onChange={() => setAnswers((a) => ({ ...a, [q.id]: points }))}
                            className="sr-only"
                          />
                          <span
                            className={`inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${selected ? "border-primary bg-primary" : "border-border"}`}
                          >
                            {selected && (
                              <span className="h-2 w-2 rounded-full bg-primary-foreground" />
                            )}
                          </span>
                          <span>{opt}</span>
                        </label>
                      );
                    })}
                  </div>
                </fieldset>
              ))}

              <div
                className="sticky bottom-4 z-10 rounded-2xl border border-border bg-card p-4 backdrop-blur"
                style={{ boxShadow: "var(--shadow-soft)" }}
              >
                <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
                  <p className="text-sm text-muted-foreground">
                    {Object.keys(answers).length}/{CONTENT_SCORE_QUESTIONS.length} answered
                  </p>
                  <button
                    onClick={handleSubmit}
                    disabled={!allAnswered || loading}
                    className="rounded-full bg-ink px-6 py-3 text-sm font-semibold text-cream transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {loading ? "Checking your content system…" : "Get my readiness score"}
                  </button>
                </div>
              </div>

              <p className="text-xs italic text-muted-foreground">
                This is a quick diagnostic, not a full strategy or audit. For a deeper review,
                contact Scott.
              </p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
